import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useNavigate } from 'react-router-dom';

const PerformanceRecommendations = ({ agentMetrics, selectedRole }) => {
  const navigate = useNavigate();

  const generateRecommendations = useMemo(() => {
    if (!agentMetrics?.length) return [];

    const filteredMetrics = selectedRole === 'all'
      ? agentMetrics
      : agentMetrics?.filter(agent => agent?.role === selectedRole);

    const recommendations = [];

    filteredMetrics?.forEach(agent => {
      // Low accuracy score recommendation
      if (agent?.accuracyScore < 90) {
        recommendations?.push({
          agentId: agent?.id,
          agentName: agent?.name,
          role: agent?.role,
          type: 'accuracy',
          priority: 'high',
          title: 'Improve Analysis Accuracy',
          description: `${agent?.name}'s accuracy score (${agent?.accuracyScore?.toFixed(1)}%) is below target. Recommend quality assurance training.`,
          suggestedCourses: ['Advanced MRV Techniques', 'Quality Control Best Practices'],
          metric: agent?.accuracyScore,
          target: 95,
          icon: 'Target'
        });
      }

      // High response time recommendation
      if (agent?.avgResponseTime > 120) {
        recommendations?.push({
          agentId: agent?.id,
          agentName: agent?.name,
          role: agent?.role,
          type: 'efficiency',
          priority: 'medium',
          title: 'Enhance Response Efficiency',
          description: `Average response time of ${agent?.avgResponseTime}s exceeds optimal range. Consider workflow optimization training.`,
          suggestedCourses: ['Time Management for Analysts', 'Workflow Automation Tools'],
          metric: agent?.avgResponseTime,
          target: 60,
          icon: 'Zap'
        });
      }

      // Low completion rate recommendation
      if (agent?.completionRate < 85) {
        recommendations?.push({
          agentId: agent?.id,
          agentName: agent?.name,
          role: agent?.role,
          type: 'completion',
          priority: 'high',
          title: 'Boost Task Completion Rate',
          description: `Completion rate of ${agent?.completionRate?.toFixed(1)}% indicates potential bottlenecks. Task management training recommended.`,
          suggestedCourses: ['Project Coordination Essentials', 'Stakeholder Communication'],
          metric: agent?.completionRate,
          target: 95,
          icon: 'CheckCircle'
        });
      }

      // Low success rate recommendation
      if (agent?.successRate < 85) {
        recommendations?.push({
          agentId: agent?.id,
          agentName: agent?.name,
          role: agent?.role,
          type: 'success',
          priority: 'high',
          title: 'Improve Project Success Rate',
          description: `Project success rate of ${agent?.successRate?.toFixed(1)}% needs improvement. Role-specific advanced training suggested.`,
          suggestedCourses: ['Advanced Methodology Application', 'Risk Mitigation Strategies'],
          metric: agent?.successRate,
          target: 90,
          icon: 'Award'
        });
      }
    });

    // Sort by priority
    return recommendations?.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder?.[a?.priority] - priorityOrder?.[b?.priority];
    });
  }, [agentMetrics, selectedRole]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-500/10 text-red-600 border-red-200';
      case 'medium': return 'bg-yellow-500/10 text-yellow-600 border-yellow-200';
      case 'low': return 'bg-blue-500/10 text-blue-600 border-blue-200';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getProgressPercentage = (metric, target) => {
    return Math.min((metric / target) * 100, 100);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-1">
            Performance-Based Recommendations
          </h2>
          <p className="text-sm text-muted-foreground">
            AI-powered training suggestions based on Agent Analytics insights
          </p>
        </div>
        <Button
          variant="outline"
          iconName="BarChart3"
          onClick={() => navigate('/agent-analytics-dashboard')}
        >
          View Analytics
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center">
              <Icon name="AlertCircle" size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {generateRecommendations?.filter(r => r?.priority === 'high')?.length}
              </p>
              <p className="text-sm text-muted-foreground">High Priority</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-500/10 rounded-lg flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} className="text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {generateRecommendations?.filter(r => r?.priority === 'medium')?.length}
              </p>
              <p className="text-sm text-muted-foreground">Medium Priority</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Users" size={20} className="text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {new Set(generateRecommendations?.map(r => r?.agentId))?.size}
              </p>
              <p className="text-sm text-muted-foreground">Agents Affected</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations List */}
      <div className="space-y-4">
        {generateRecommendations?.map((rec, idx) => (
          <div
            key={idx}
            className="bg-card border border-border rounded-lg p-5 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-4 flex-1">
                <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name={rec?.icon} size={24} className="text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-base font-semibold text-foreground">{rec?.title}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-medium border ${getPriorityColor(rec?.priority)}`}>
                      {rec?.priority?.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{rec?.description}</p>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Icon name="User" size={14} className="text-muted-foreground" />
                      <span className="text-muted-foreground">{rec?.agentName}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="Briefcase" size={14} className="text-muted-foreground" />
                      <span className="text-muted-foreground capitalize">{rec?.role}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress to Target */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Progress to Target</span>
                <span className="text-sm text-muted-foreground">
                  Current: {rec?.metric?.toFixed(1)} | Target: {rec?.target}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="h-2 rounded-full bg-primary"
                  style={{ width: `${getProgressPercentage(rec?.metric, rec?.target)}%` }}
                />
              </div>
            </div>

            {/* Suggested Courses */}
            <div className="mb-4">
              <p className="text-sm font-medium text-foreground mb-2">Suggested Training:</p>
              <div className="flex flex-wrap gap-2">
                {rec?.suggestedCourses?.map((course, courseIdx) => (
                  <span
                    key={courseIdx}
                    className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium"
                  >
                    {course}
                  </span>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button variant="default" size="sm" iconName="BookOpen">
                Assign Training
              </Button>
              <Button variant="outline" size="sm" iconName="Calendar">
                Schedule Review
              </Button>
            </div>
          </div>
        ))}
      </div>

      {generateRecommendations?.length === 0 && (
        <div className="text-center py-12 bg-muted/30 rounded-lg">
          <Icon name="CheckCircle" size={48} className="mx-auto mb-4 text-green-500" />
          <p className="text-foreground font-medium mb-1">All agents performing well!</p>
          <p className="text-sm text-muted-foreground">No training recommendations at this time</p>
        </div>
      )}
    </div>
  );
};

export default PerformanceRecommendations;